// pages/creator-panel/Channels/WhatsappGroup/GroupMessagesModal.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Pagination, Chip, Divider,
} from "@mui/material";

export default function GroupMessagesModal({ group, permission, onNotify, onClose }) {
  const { chatbotId } = useParams();
  const params = chatbotId ? { chatbot_id: chatbotId } : {};

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [training, setTraining] = useState(false);
  const [changed, setChanged] = useState(false);

  const fetchMessages = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/whatsapp-group/groups/${group.id}/messages`, {
        params: { ...params, page: p, per_page: 25 },
      });
      if (res.data.success) {
        setMessages(res.data.data);
        setTotalPages(res.data.pagination.total_pages || 1);
      }
    } catch (err) {
      if (onNotify) onNotify("error", err.response?.data?.message || "Failed to load messages.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  useEffect(() => { fetchMessages(page); /* eslint-disable-next-line */ }, [page]);

  const handleTrain = async () => {
    setTraining(true);
    try {
      const res = await axiosInstance.post(`/api/whatsapp-group/groups/${group.id}/train`, {}, { params });
      if (onNotify) onNotify(res.data.success ? "success" : "error", res.data.message);
      if (res.data.success) setChanged(true);
    } catch (err) {
      if (onNotify) onNotify("error", err.response?.data?.message || "Training failed.");
    } finally {
      setTraining(false);
    }
  };

  return (
    <Dialog open onClose={() => onClose(changed)} maxWidth="md" fullWidth>
      <DialogTitle>
        {group.group_name}
        <Chip size="small" sx={{ ml: 1 }} label={group.is_trained ? "Trained" : "Not trained"}
          color={group.is_trained ? "primary" : "default"} />
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {group.message_count || 0} messages · {group.tokens || 0} tokens
          {group.anonymized ? " · member numbers redacted" : ""}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        ) : messages.length === 0 ? (
          <Typography color="text.secondary">No messages.</Typography>
        ) : (
          <Box>
            {messages.map((m) => (
              <Box key={m.id} sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {m.sender_name || "Unknown"}{m.message_timestamp ? ` · ${new Date(m.message_timestamp).toLocaleString()}` : ""}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{m.message_text}</Typography>
              </Box>
            ))}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} size="small" />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {permission.canModify && (
          <Button onClick={handleTrain} variant="contained" disabled={training} sx={{ bgcolor: "#25D366" }}>
            {training ? <><CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />Training…</>
              : (group.is_trained ? "Retrain" : "Train into knowledge base")}
          </Button>
        )}
        <Button onClick={() => onClose(changed)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
